/** @file Reads rig attachments: region quads, mesh UVs and skins, handles mapped into slot-bone space. */
import type { AtlasJson, AtlasRegion, Pose2DJson } from '@pose2d/format/types';
import type { AttachmentData, MeshAttachmentData, MeshSkinData, RegionAttachmentData } from '../../skeletonData';
import { handleToLocal } from '../../load/handleToLocal';
import { meshPixelToLocal } from '../../load/meshPixelToLocal';
import { regionPixelToLocal } from '../../load/regionPixelToLocal';
import { regionUv } from '../../load/regionUv';
import { coreFail } from '../coreFail';
import { indexIn } from './indexIn';
import { packHex } from './packHex';
import { readMeshSkin } from './readMeshSkin';
import { regionCorners } from './regionCorners';

type RigAttachment = Pose2DJson['attachments'][number];
type RigRegion = Extract<RigAttachment, { type: 'region' }>;
type RigMesh = Extract<RigAttachment, { type: 'mesh' }>;
type Page = AtlasJson['pages'][number];

const WHITE = '#ffffffff';

function readRegion(att: RigRegion, region: AtlasRegion, page: Page, path: string): RegionAttachmentData {
  const toLocal = regionPixelToLocal({
    x: att.x ?? 0, y: att.y ?? 0, rotation: att.rotation ?? 0, scaleX: att.scaleX ?? 1, scaleY: att.scaleY ?? 1,
    w: att.w, h: att.h, origW: region.origW, origH: region.origH,
  });
  const { local, uvs } = regionCorners(toLocal, region, page.w, page.h);
  return {
    type: 'region', name: att.name, image: att.image, color: packHex(att.color ?? WHITE, `${path}.color`), local, uvs,
    page: region.page, handle: att.handle === undefined ? null : handleToLocal(att.handle, toLocal), fitLength: att.fitLength ?? false,
  };
}

function readSkin(att: RigMesh, vertexCount: number, boneCount: number, path: string): MeshSkinData {
  if (att.vertices === undefined) return readMeshSkin(att.skin ?? [], vertexCount, boneCount, `${path}.skin`);
  if (att.vertices.length !== att.uvs.length) {
    coreFail('VALIDATION', `${att.vertices.length} vertex numbers for ${att.uvs.length} uv numbers`, { path: `${path}.vertices` });
  }
  return { kind: 'rigid', vertices: Float64Array.from(att.vertices) };
}

function readMesh(att: RigMesh, region: AtlasRegion, page: Page, path: string, boneCount: number): MeshAttachmentData {
  const vertexCount = att.uvs.length / 2;
  if (!Number.isInteger(vertexCount)) coreFail('VALIDATION', 'uvs must hold u, v pairs', { path: `${path}.uvs` });
  const uvs = new Float32Array(att.uvs.length);
  regionUv(uvs, att.uvs, region, page.w, page.h);
  const skin = readSkin(att, vertexCount, boneCount, path);
  return {
    type: 'mesh', name: att.name, image: att.image, color: packHex(att.color ?? WHITE, `${path}.color`), uvs,
    triangles: Uint16Array.from(att.tris, (t, k) => indexIn(t, vertexCount, `${path}.tris[${k}]`)),
    hull: indexIn(att.hull, vertexCount + 1, `${path}.hull`),
    page: region.page,
    skin,
    handle: att.handle === undefined ? null : handleToLocal(att.handle, meshPixelToLocal(att.uvs, region.origW, region.origH, skin.kind === 'rigid' ? skin.vertices : null)),
    fitLength: att.fitLength ?? false,
  };
}

/**
 * Attachments in rig order. Each image needs an atlas region (VALIDATION at `attachments[i].image`). Handles are
 * converted to slot-bone space (y-up): regions through their transform, rigid meshes through a least-squares
 * pixel -> vertex fit, skinned meshes (never mountable) through the centred image frame.
 */
export function readAttachments(attachments: Pose2DJson['attachments'], atlas: AtlasJson, boneCount: number): AttachmentData[] {
  return attachments.map((att, i): AttachmentData => {
    const path = `attachments[${i}]`;
    const type: string = att.type;
    if (type !== 'region' && type !== 'mesh') coreFail('VALIDATION', `unknown attachment type "${type}"`, { path: `${path}.type` });
    const region = atlas.regions[att.image] ?? coreFail('VALIDATION', `the atlas has no region for image "${att.image}"`, { path: `${path}.image` });
    const page = atlas.pages[region.page] ?? coreFail('VALIDATION', `the atlas has no page ${region.page}`, { path: `${path}.image` });
    return att.type === 'region' ? readRegion(att, region, page, path) : readMesh(att, region, page, path, boneCount);
  });
}
